import { useState, useCallback, useEffect, useRef } from 'react';
import { circlesAPI, getToken } from '../services/api';

// Realtime comes from the SSE stream; this poll is only a FALLBACK that kicks
// in while the stream is disconnected (it re-connects itself via effect re-runs).
const FALLBACK_POLL_MS = 12000;

/**
 * Group-chat data for a single circle. This is the SSE machinery that used to
 * live inside CirclesScreen, lifted into a hook so the in-chat CircleChatArea
 * can reuse it (fetch-based SSE — EventSource can't send the JWT — with a 3s
 * reconnect and a 12s poll fallback while the stream is down).
 *
 * @param {number|null} circleId - active circle, or null in personal mode (no-op)
 */
export function useCircleChat(circleId) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sophiaThinking, setSophiaThinking] = useState(false);
  const lastIdRef = useRef(0);
  const streamLiveRef = useRef(false);
  const reqRef = useRef(0); // guards against a stale circle's fetch clobbering a newer one

  // Append with id-dedupe (stream + fallback poll can both deliver a message).
  const appendMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages(prev => {
      const known = new Set(prev.map(m => m.id));
      const fresh = incoming.filter(m => m.id && !known.has(m.id));
      if (fresh.length === 0) return prev;
      lastIdRef.current = fresh.reduce((max, m) => Math.max(max, m.id), lastIdRef.current);
      return [...prev, ...fresh];
    });
  }, []);

  const reload = useCallback(async () => {
    if (!circleId) return;
    const seq = ++reqRef.current;
    setLoading(true);
    try {
      const data = await circlesAPI.getMessages(circleId, 0);
      if (seq !== reqRef.current) return; // a newer circle took over — drop this
      const msgs = data.messages || [];
      setMessages(msgs);
      lastIdRef.current = msgs.reduce((max, m) => Math.max(max, m.id), 0);
    } catch (e) { /* transient — the stream/poll will catch up */ }
    finally { if (seq === reqRef.current) setLoading(false); }
  }, [circleId]);

  // Reset + initial load whenever the active circle changes
  useEffect(() => {
    setMessages([]);
    setSophiaThinking(false);
    lastIdRef.current = 0;
    streamLiveRef.current = false;
    if (circleId) { setLoading(true); reload(); }
    else setLoading(false);
  }, [circleId, reload]);

  // Realtime chat: SSE stream (fetch-based — EventSource can't send the JWT).
  useEffect(() => {
    if (!circleId) return undefined;
    const controller = new AbortController();
    let stopped = false;

    (async () => {
      while (!stopped) {
        try {
          const response = await fetch(circlesAPI.streamUrl(circleId), {
            headers: { 'Authorization': `Bearer ${getToken()}` },
            signal: controller.signal
          });
          if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);
          streamLiveRef.current = true;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const chunks = buffer.split('\n\n');
            buffer = chunks.pop(); // keep the trailing partial chunk
            for (const chunk of chunks) {
              const line = chunk.split('\n').find(l => l.startsWith('data: '));
              if (!line) continue;
              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === 'message' && event.message) {
                  appendMessages([event.message]);
                  if (event.message.role === 'assistant') setSophiaThinking(false);
                } else if (event.type === 'thinking') {
                  setSophiaThinking(true);
                } else if (event.type === 'idle') {
                  setSophiaThinking(false);
                }
              } catch (e) { /* malformed chunk — skip */ }
            }
          }
        } catch (e) {
          if (stopped) break;
        }
        streamLiveRef.current = false;
        // brief backoff, then reconnect
        await new Promise(r => setTimeout(r, 3000));
      }
    })();

    // Fallback poll — only does work while the stream is down
    const timer = setInterval(async () => {
      if (streamLiveRef.current) return;
      try {
        const data = await circlesAPI.getMessages(circleId, lastIdRef.current);
        appendMessages(data.messages || []);
      } catch (e) { /* transient poll errors are fine */ }
    }, FALLBACK_POLL_MS);

    return () => {
      stopped = true;
      streamLiveRef.current = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [circleId, appendMessages]);

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending || !circleId) return;
    setSending(true);
    try {
      // Returns just the saved human message — Sophia's reply (if any) arrives
      // via the stream ('thinking' → 'message'), so sending feels instant.
      const data = await circlesAPI.sendMessage(circleId, trimmed);
      appendMessages(data.messages || []);
    } finally {
      setSending(false);
    }
  }, [circleId, sending, appendMessages]);

  return { messages, loading, sending, sophiaThinking, send, reload };
}
